"use client";

import Link from "next/link";
import Button from "../ui/Button";
import Image from "next/image";
import BenefitCard from "../ui/BenefitCard";
import FlashIcon from "../icons/FlashIcon";
import LicenseIcon from "../icons/LicenseIcon";
import AwardIcon from "../icons/AwardIcon";
import CreditCardIcon from "../icons/CreditCardIcon";
import DeliveryTruckIcon from "../icons/DeliveryTruckIcon";
import ShopAllIcon from "../icons/ShopAllIcon";
import PhoneIcon from "../icons/PhoneIcon";
import GiftsIcon from "../icons/GiftsIcon";

const BENEFITS = [
  {
    icon: <FlashIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Instant menu access",
    description: "Browse our cannabis menu with no login or ID verification",
  },
  {
    icon: <LicenseIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Safe ordering",
    description: "We are fully AGLC inspected, licensed, and registered in Alberta",
  },
  {
    icon: <AwardIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Trusted products",
    description: "Government of Canada certified & tested products",
  },
  {
    icon: <CreditCardIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Secure payments",
    description: "Easily pay for your order online using your credit card",
  },
  {
    icon: <DeliveryTruckIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Quick delivery",
    description: "Choose fast same day delivery or reliable Xpresspost shipping",
  },
  {
    icon: <ShopAllIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Biggest selection",
    description: "Expertly curated menu with best brands & products",
  },
  {
    icon: <PhoneIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Customer service",
    description: "Easily track your order status via direct link and SMS",
  },
  {
    icon: <GiftsIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Dank club",
    description: "Earn points on every purchase and swap them for rewards",
  },
] as const;

export default function WhyDankDeliverySection() {
  return (
    <section className="w-full px-4 sm:px-6 lg:px-8" aria-label="Why Dank weed delivery">
      <div className="w-full">
        {/* Mobile: neutral panel + horizontal scroll; each slide is 2 cards tall (2 rows). lg+: 4-column grid. */}
        <div className="max-lg:rounded-2xl">
          <h2 className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary">
            Why Dank weed delivery?
          </h2>

          <div className="mt-4 max-lg:-mx-4 max-lg:px-4 sm:max-lg:-mx-6 sm:max-lg:px-6">
            {/* Mobile / tablet: scroll horizontally; each column = 2 rows (two stacked cards). */}
            <div
              className="flex gap-3 overflow-x-auto scroll-smooth pb-1 scrollbar-hide snap-x snap-mandatory lg:hidden"
              role="region"
              aria-label="Delivery benefits"
            >
              {Array.from({ length: BENEFITS.length / 2 }, (_, col) => {
                const top = BENEFITS[col * 2];
                const bottom = BENEFITS[col * 2 + 1];
                return (
                  <div
                    key={top.title}
                    className="flex w-[min(280px,calc(100vw-4rem))] shrink-0 snap-start flex-col gap-3 sm:w-[280px]"
                  >
                    <BenefitCard
                      icon={top.icon}
                      title={top.title}
                      description={top.description}
                    />
                    <BenefitCard
                      icon={bottom.icon}
                      title={bottom.title}
                      description={bottom.description}
                    />
                  </div>
                );
              })}
            </div>

            {/* Desktop: unchanged 4-column grid (2 rows, no horizontal scroll). */}
            <div className="hidden lg:grid lg:grid-cols-4 lg:gap-4">
              {BENEFITS.map((b) => (
                <BenefitCard
                  key={b.title}
                  icon={b.icon}
                  title={b.title}
                  description={b.description}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 relative w-full rounded-2xl overflow-hidden bg-[#FFD2D2]">
          <div className="absolute inset-0">
            <Image
              src="/media/sections/lifestyle/backgrounds/banner-background-shape.webp"
              alt=""
              fill
              className="object-cover"
              aria-hidden="true"
            />
          </div>

          <div className="relative z-10 sm:px-6 px-4 flex flex-col lg:flex-row items-center justify-between gap-4 sm:pb-6 pb-4">
            <div className="h-48 w-full overflow-hidden flex items-center justify-center sm:hidden">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="w-full object-center"
                aria-hidden="true"
              />
            </div>
            <div className="h-32 overflow-hidden max-sm:hidden -mb-8">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="object-top -mt-6"
                aria-hidden="true"
              />
            </div>

            <div className="flex-1 sm:mt-7">
              <h3 className="text-body-20 font-bold text-text-primary mb-1 leading-tight">
                Enjoy a high country life
              </h3>
              <p className="text-body-16 font-medium text-text-secondary leading-relaxed">
                Gummies, chocolates and drinks from Canada&apos;s best makers, ready for pickup or same day delivery.
              </p>
            </div>

            <Button asChild variant="primary" size="lg" className="sm:w-fit w-full px-6 py-3 rounded-xl mt-6">
              <Link href="/search?category=Edibles">
                Shop our new edibles
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

